/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import { Option } from "core/types";

const EmptyString = "";
export const DefaultOption = { label: "", value: "", isDisabled: false } as Option;
export const DefaultOptions = [DefaultOption] as Option[];

export const nullToEmptyString = (value: any | null): any | string => {
  return value ? value : EmptyString;
};

export const nullToBoolean = (value: any | null): any | boolean => {
  return value ? value : false;
};

export const nullToEmptyArray = (value: any | null): any | boolean => {
  return value ? value : [];
};

export const nullToObject = (value: any | null): any | boolean => {
  return value ? {} : value;
};

export const nanToNumber = (value: any | null): any | boolean => {
  return isNaN(value) ? undefined : value;
};

export const emptyStringToNull = (value: any, originalValue: any) => {
  if (typeof originalValue === "string" && originalValue === "") {
    return null;
  }
  return value;
};

export const toOption = <T>(item: T, value_property: string, label_property: string, isDisabled: boolean): Option => {
  if (item) {
    return { value: item[value_property], label: item[label_property], isDisabled: isDisabled };
  } else {
    return DefaultOption;
  }
};

export const toOptions = <T>(list: Array<T>, value_property: string, label_property: string): Option[] => {
  return list.map((item) => {
    return toOption<T>(item, value_property, label_property, false);
  });
};

export const findSelected = (options: Option[], value: number | string | undefined): Option => {
  if (value) {
    return options.find((option) => option.value == value);
  }
  return DefaultOption;
};

export const findMultipleSelected = (options: Option[], value: number | string): Option[] | [] => {
  if (value) {
    return options.filter((option) => option.value == value);
  }
  return DefaultOptions;
};

export const isNullOrEmpty = (value: string | null): boolean => {
  return !value;
};

export const toTitleCase = (value: string | null): string => {
  if (value) {
    return value.replace("_", " ").replace(/\w\S*/g, function (s) {
      return s.charAt(0).toUpperCase() + s.substr(1).toLowerCase();
    });
  } else {
    return EmptyString;
  }
};

export const range = (start: number | null, end: number | null, step = 1): Array<number> => {
  if (start == null || end == null) {
    return [];
  }
  return Array.from({ length: (end - start) / step + 1 }, (_, i) => start + i * step);
};

export const toRangeOptions = (start: number | null, end: number | null): Array<Option> => {
  if (start == null || end == null) {
    return [];
  }
  return Array(end - start + 1)
    .fill()
    .map((_, index) => {
      const value = start + index;
      return {
        value: value.toString(),
        label: value.toString().padStart(2, "0")
      };
    });
};

export const removeEmpty = (obj) => {
  return Object.entries(obj)
    .filter(([_, v]) => v != null)
    .reduce((acc, [k, v]) => ({ ...acc, [k]: v === Object(v) ? removeEmpty(v) : v }), {});
};

export const objectMap = (obj, fn) => Object.fromEntries(Object.entries(obj).map(([k, v], i) => [k, fn(v, k, i)]));

export const toBase64 = (file, callback) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = function () {
    callback(reader.result);
  };
  reader.onerror = function (error) {
    console.error("Error: ", error);
  };
};

export const toDataUrl = (url) =>
  fetch(url)
    .then((response) => response.blob())
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    );

// TODO: Integrate Rails i18n
export const translate = (documentType: string): string => {
  switch (documentType) {
    case "work_order": {
      return "Signed Work Order Form";
    }
    case "approved_plans": {
      return "Approved Plans";
    }
    case "construction_drawing": {
      return "Construction Drawing";
    }
    case "bill_of_materials": {
      return "Bill of Materials";
    }
    case "reference": {
      return "Document Reference";
    }
    default: {
      return "Unknown Document Type";
    }
  }
};

export const replaceKeysInObj = (obj, key, newKey) => {
  const newObj = {};
  for (const ogKey in obj) {
    if (ogKey === key) {
      newObj[newKey] = obj[ogKey];
    } else if (typeof obj[ogKey] === "object") {
      newObj[ogKey] = replaceKeysInObj(obj[ogKey], key, newKey);
    } else {
      newObj[ogKey] = obj[ogKey];
    }
  }
  return newObj;
};
